import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/client/api';
import { Button, Card, ErrorNotice, Field, Input } from '@/client/components/ui';
import { SESSION_KEY, WORKSPACE_KEY } from '@/client/store';
import type { Workspace } from '@/shared/types';

/** First run: create the spreadsheet that becomes the source of truth. */
export function Setup() {
  const queryClient = useQueryClient();
  const [riderName, setRiderName] = useState('');

  const guessedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

  const create = useMutation({
    mutationFn: () =>
      api.bootstrap({ riderName: riderName.trim() || 'Rider', timezone: guessedTimezone }),
    onSuccess: (workspace: Workspace) => {
      queryClient.setQueryData(WORKSPACE_KEY, workspace);
      void queryClient.invalidateQueries({ queryKey: SESSION_KEY });
    },
  });

  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-10">
      <Card className="w-full max-w-md p-6">
        <h1 className="text-xl font-semibold tracking-tight">Set up your workspace</h1>
        <p className="mt-2 text-sm text-ink-muted">
          This creates one spreadsheet in your Google Drive with tabs for volunteers, riders,
          assignments, and settings. It stays yours: you can open and edit it directly at any time,
          and this app reads and writes the same file.
        </p>

        <div className="mt-6 flex flex-col gap-4">
          <Field
            label="Who needs rides?"
            hint="Just a first name is fine. You can add more riders later."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={riderName}
                autoComplete="off"
                placeholder="Marie"
                onChange={(event) => setRiderName(event.target.value)}
              />
            )}
          </Field>

          <p className="text-xs text-ink-faint">
            Timezone detected as{' '}
            <span className="font-medium text-ink-muted">{guessedTimezone}</span>. You can change it
            in Settings.
          </p>

          {create.isError ? <ErrorNotice error={create.error} /> : null}

          <Button
            variant="primary"
            fullWidth
            busy={create.isPending}
            onClick={() => create.mutate()}
          >
            Create my workspace
          </Button>
        </div>
      </Card>
    </main>
  );
}
